/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from "react";
import { StyleSheet, css } from "aphrodite";
import { useMediaQuery } from "@mui/material";
import Dashboard from "../../../services/api/dashboard";

const styles = StyleSheet.create({
  card: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    marginTop: 16,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    },
    "@media (max-width: 600px)": {
      borderRadius: 12,
      marginTop: 12,
    },
  },
  sectionContainer: {
    padding: "16px 24px",
    "@media (max-width: 600px)": {
      padding: "12px 16px",
    },
  },
  divider: {
    height: 1,
    backgroundColor: "#f0f0f0",
    margin: "20px 0",
    "@media (max-width: 600px)": {
      margin: "16px 0",
    },
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    "@media (max-width: 600px)": {
      marginBottom: 12,
    },
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
    "@media (max-width: 600px)": {
      fontSize: 15,
    },
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    boxSizing: "border-box",
    "@media (max-width: 600px)": {
      margin: "0 -8px",
      padding: "0 8px",
      maxWidth: "calc(100% + 16px)",
    },
  },
  table: {
    width: "100%",
    minWidth: "100%",
    borderCollapse: "separate",
    borderSpacing: "0px",
    backgroundColor: "#ffffff",
  },
  headerRow: {
    background: "#1a237e",
  },
  headerCell: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    textAlign: "center",
    border: "1px solid #303f9f",
    borderBottom: "1px solid #303f9f",
    whiteSpace: "nowrap",
    ":first-child": {
      textAlign: "center",
    },
    "@media (max-width: 600px)": {
      padding: "10px 3px",
      fontSize: 10,
    },
  },
  dataRow: {
    backgroundColor: "#ffffff",
    ":hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  cell: {
    padding: "12px 16px",
    fontSize: 14,
    color: "#6b7280",
    border: "1px solid #e5e7eb",
    borderTop: "none",
    textAlign: "center",
    fontWeight: 500,
    whiteSpace: "nowrap",
    ":first-child": {
      textAlign: "center",
      color: "#4b5563",
      fontWeight: 600,
    },
    "@media (max-width: 600px)": {
      padding: "6px 3px",
      fontSize: 12,
    },
  },
  scrollHint: {
    display: "none",
    "@media (max-width: 600px)": {
      display: "block",
      textAlign: "center",
      fontSize: 11,
      color: "#999",
      marginTop: 8,
      fontStyle: "italic",
    },
  },
});

function OrganizationStatistics() {
  const [orgStats, setOrgStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await Dashboard.getOrganizationStatistics();
        // console.log("API Response:", response); // check exact structure
        const data = response?.data || {};
        setOrgStats(data);
      } catch (err) {
        // setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!orgStats) return null;

  const organizationData = {
    headers: [
      "Area",
      "Cluster",
      "Zone",
      "Region",
      "Chapter",
      "District",
      "Block",
    ],

    data: [
      orgStats.areaCount,
      orgStats.clusterCount,
      orgStats.zoneCount,
      orgStats.regionCount,
      orgStats.chapterCount,
      orgStats.districtCount,
      orgStats.blockCount,
    ],
  };

  const leadershipData = {
    headers: ["AC", "CLC", "ZC", "RC", "CC", "DC", "BC", "TOTAL"],
    data: [
      orgStats.areaChiefCount?.mdcount ?? 0,
      orgStats.clusterChiefCount?.mdcount ?? 0,
      orgStats.zoneChiefCount?.mdcount ?? 0,
      orgStats.regionChiefCount?.mdcount ?? 0,
      orgStats.chapterChiefCount?.mdcount ?? 0,
      orgStats.districtChiefCount?.mdcount ?? 0,
      orgStats.blockChiefCount?.mdcount ?? 0,
      (orgStats.areaChiefCount?.mdcount ?? 0) +
        (orgStats.clusterChiefCount?.mdcount ?? 0) +
        (orgStats.zoneChiefCount?.mdcount ?? 0) +
        (orgStats.regionChiefCount?.mdcount ?? 0) +
        (orgStats.chapterChiefCount?.mdcount ?? 0) +
        (orgStats.districtChiefCount?.mdcount ?? 0) +
        (orgStats.blockChiefCount?.mdcount ?? 0),
    ],
  };

  return (
    <div className={css(styles.card)}>
      {/* Organization Statistics Section */}
      <div className={css(styles.sectionContainer)}>
        <div className={css(styles.titleRow)}>
          <h3 className={css(styles.title)}>Organization Statistics</h3>
        </div>

        <div className={css(styles.tableWrapper)}>
          <table className={css(styles.table)}>
            <thead>
              <tr className={css(styles.headerRow)}>
                {organizationData.headers.map((header) => (
                  <th key={header} className={css(styles.headerCell)}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className={css(styles.dataRow)}>
                {organizationData.data.map((value, index) => (
                  <td key={index} className={css(styles.cell)}>
                    {value === 0 ? "-" : value}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        {isMobile && (
          <div className={css(styles.scrollHint)}>← Swipe to view more →</div>
        )}
      </div>

      {/* Divider */}
      <div className={css(styles.divider)} />

      {/* Leadership Statistics Section */}
      <div className={css(styles.sectionContainer)}>
        <div className={css(styles.titleRow)}>
          <h3 className={css(styles.title)}>Leadership Statistics</h3>
        </div>

        <div className={css(styles.tableWrapper)}>
          <table className={css(styles.table)}>
            <thead>
              <tr className={css(styles.headerRow)}>
                {leadershipData.headers.map((header) => (
                  <th key={header} className={css(styles.headerCell)}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className={css(styles.dataRow)}>
                {leadershipData.data.map((value, index) => (
                  <td key={index} className={css(styles.cell)}>
                    {value}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        {isMobile && (
          <div className={css(styles.scrollHint)}>← Swipe to view more →</div>
        )}
      </div>
    </div>
  );
}

export default OrganizationStatistics;
